import { jsx as _jsx } from "react/jsx-runtime";
// Authentication context provider
import { createContext, useCallback, useEffect, useState } from "react";
import { useCurrentUser, useLogin, useVerifyOTP, useLogout } from "../api/auth";
import { AuthService } from "../services/auth";
// Create auth context
export const AuthContext = createContext(null);
// Auth service instance
const authService = new AuthService();
/**
 * Authentication provider component
 * Manages global authentication state
 */
export function AuthProvider({ children }) {
    const [authState, setAuthState] = useState({
        user: null,
        isAuthenticated: false,
        isLoading: true,
        error: null,
    });
    // Use SWR hooks for data fetching
    const { user, session, isLoading: userLoading, error: userError, refreshUser } = useCurrentUser();
    const { login: loginApi } = useLogin();
    const { verifyOTP: verifyOTPApi } = useVerifyOTP();
    const { logout: logoutApi } = useLogout();
    // Update auth state when user data changes
    useEffect(() => {
        setAuthState((prev) => ({
            ...prev,
            user,
            isAuthenticated: !!user,
            isLoading: userLoading,
            error: userError,
        }));
    }, [user, userLoading, userError]);
    // Login function
    const login = useCallback(async (phoneNumber) => {
        setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));
        try {
            await loginApi(phoneNumber);
            // Don't update auth state here - wait for OTP verification
        }
        catch (error) {
            setAuthState((prev) => ({
                ...prev,
                isLoading: false,
                error: error instanceof Error ? error.message : "Login failed",
            }));
            throw error;
        }
    }, [loginApi]);
    // Verify OTP function
    const verifyOTP = useCallback(async (phoneNumber, code) => {
        setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));
        try {
            const response = await verifyOTPApi(phoneNumber, code);
            // Update auth state with user data
            setAuthState((prev) => ({
                ...prev,
                user: response.user,
                isAuthenticated: true,
                isLoading: false,
                error: null,
            }));
            // Refresh user data to get session info
            await refreshUser();
        }
        catch (error) {
            setAuthState((prev) => ({
                ...prev,
                isLoading: false,
                error: error instanceof Error ? error.message : "OTP verification failed",
            }));
            throw error;
        }
    }, [verifyOTPApi, refreshUser]);
    // Logout function
    const logout = useCallback(async () => {
        setAuthState((prev) => ({ ...prev, isLoading: true }));
        try {
            await logoutApi();
            // Reset auth state
            setAuthState({
                user: null,
                isAuthenticated: false,
                isLoading: false,
                error: null,
            });
        }
        catch (error) {
            // Even if logout fails, reset local state
            setAuthState({
                user: null,
                isAuthenticated: false,
                isLoading: false,
                error: error instanceof Error ? error.message : "Logout failed",
            });
        }
    }, [logoutApi]);
    // Context value
    const contextValue = {
        ...authState,
        login,
        verifyOTP,
        logout,
        refreshUser,
    };
    return _jsx(AuthContext.Provider, { value: contextValue, children: children });
}
